import InfoIcon from '@mui/icons-material/Info';
import { Box, IconButton, List, ListItem, ListItemAvatar, ListItemText, Typography } from '@mui/material';
import { useState } from 'react';

import DescriptionModal from '..';
import { AdvertisedSocialGroup, InstanceInfo, SocialGroupInfo } from '../../../../Types/AuthServer';
import AvatarImageWrapper from '../../../Avatars/AvatarImageWrapper';
import { dayjs } from '../../../Dayjs/dayjs-utils';
import { UploadType } from '../../../Image/UploadType';

interface GroupTabProps {
    domain: string | undefined;
    instanceInfo: InstanceInfo | undefined;
    social_groups: AdvertisedSocialGroup[] | undefined;
}

export default function GroupTab({ domain, instanceInfo, social_groups }: Readonly<GroupTabProps>) {
    const [modalDataId, setModalDataId] = useState('');
    const [open, setOpen] = useState(false);
    const handleOpen = (id: string) => {
        setOpen(true);
        setModalDataId(id);
    };
    const handleClose = () => {
        setOpen(false);
    };

    return social_groups && social_groups.length > 0 ? (
        <Box>
            <List disablePadding sx={{ bgcolor: 'background.paper', width: '100%' }}>
                {social_groups.map((item) => (
                    <ListItem
                        disablePadding
                        key={item.id}
                        secondaryAction={
                            <IconButton edge="end" onClick={() => handleOpen(item.id)}>
                                <InfoIcon color="primary" />
                            </IconButton>
                        }
                    >
                        <ListItemAvatar>
                            <AvatarImageWrapper imagePath={item.image} imageType={UploadType.Group} isLoading={false} />
                        </ListItemAvatar>
                        <ListItemText primary={item.name} secondary={<DisplayActiveTime data={item} />} />
                    </ListItem>
                ))}
            </List>
            <DescriptionModal
                activeTime={
                    <DisplayActiveTime data={social_groups.find((group) => group.id === modalDataId)} oneliner />
                }
                excludedKeys={[
                    'id',
                    'creator',
                    'utc_start',
                    'utc_end',
                    'enable_days',
                    'public',
                    'links',
                    'auto_joinable',
                ]}
                hideTC
                instanceDescription={social_groups.find((group) => group.id === modalDataId)}
                noMap
                onClose={handleClose}
                open={open}
            />
        </Box>
    ) : (
        <Box>
            <Typography>No advertised groups</Typography>
        </Box>
    );
}

const isCurrentlyActive = (
    activeDays: number[] | undefined,
    activeStartTime: string | undefined,
    activeEndTime: string | undefined,
): boolean => {
    const currentDay = dayjs().day();
    const currentTimeUTC = dayjs().utc().format('HH:mm:ss');

    if (activeDays?.includes(currentDay)) {
        const activeStartTimeObj = dayjs(activeStartTime, 'HH:mm:ss');
        let activeEndTimeObj = dayjs(activeEndTime, 'HH:mm:ss');

        if (activeEndTime?.startsWith('1 ')) {
            activeEndTimeObj = activeEndTimeObj.add(1, 'day');
        }

        const currentTimeUTCObj = dayjs(currentTimeUTC, 'HH:mm:ss');

        return currentTimeUTCObj.isBetween(activeStartTimeObj, activeEndTimeObj, null, '[]');
    }

    return false;
};

function DisplayActiveTime({
    data,
    oneliner,
}: {
    readonly data: SocialGroupInfo | undefined;
    readonly oneliner?: boolean;
}) {
    const isPublic = data?.public;
    const start = data?.utc_start;
    const end = data?.utc_end;
    const daysEnabled = data?.enable_days;

    const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const displayDays =
        daysEnabled?.length === 7 ? 'Daily' : daysEnabled?.map((dayNumber) => weekdays[dayNumber - 1]).join(', ');

    const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const startingTime = dayjs.utc(start, 'HH:mm');
    const endingTime = end === '1 00:00:00' ? startingTime.add(1, 'day') : dayjs.utc(end, 'HH:mm');

    const localStart = startingTime.tz(localTimezone).format('HH:mm');
    const localEnd = endingTime.tz(localTimezone).format('HH:mm');

    const isActiveNow = isCurrentlyActive(daysEnabled, start, end);

    return isPublic ? (
        <span>
            <span style={{ color: start ? (isActiveNow ? '#44b700' : '#d32f2f') : '#d32f2f', marginRight: 4 }}>
                {localStart} - {localEnd}
            </span>
            {!oneliner && <br />}
            <span>{displayDays}</span>
        </span>
    ) : (
        'Private'
    );
}
